import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, isCartOpen, setCartOpen, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#111111]/95 backdrop-blur-xl saturate-150"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-6">
              <h2 className="font-mono text-xs tracking-[0.3em] text-[#F9F6F0]">RESEARCH CART</h2>
              <button
                data-testid="cart-close-btn"
                onClick={() => setCartOpen(false)}
                className="text-[#A3A3A3] transition-colors hover:text-[#00ff72]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div data-testid="cart-empty-state" className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <p className="font-display text-2xl text-[#A3A3A3]">Your cart is empty</p>
                  <p className="font-mono text-[10px] tracking-[0.2em] text-[#666666]">
                    ADD COMPOUNDS FROM THE CATALOG
                  </p>
                </div>
              ) : (
                <ul className="space-y-5">
                  {items.map((item) => (
                    <li key={item.key} data-testid={`cart-item-${item.productId}`} className="flex gap-4 border border-white/10 bg-black/40 p-4">
                      <img src={item.image} alt={item.name} className="h-16 w-16 shrink-0 object-cover grayscale" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-display text-base text-[#F9F6F0]">{item.name}</p>
                            <p className="font-mono text-[10px] tracking-widest text-[#666666]">{item.dosage}</p>
                          </div>
                          <button
                            data-testid={`cart-remove-${item.productId}`}
                            onClick={() => removeItem(item.key)}
                            className="text-[#666666] transition-colors hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-white/15">
                            <button
                              data-testid={`cart-qty-minus-${item.productId}`}
                              onClick={() => updateQty(item.key, -1)}
                              className="px-2 py-1 text-[#A3A3A3] hover:text-[#00ff72]"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span data-testid={`cart-qty-${item.productId}`} className="px-3 font-mono text-xs">{item.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${item.productId}`}
                              onClick={() => updateQty(item.key, 1)}
                              className="px-2 py-1 text-[#A3A3A3] hover:text-[#00ff72]"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="font-mono text-sm text-[#F9F6F0]">
                            ${(item.price * item.qty).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 p-6">
                <p className="mb-4 font-mono text-[9px] leading-relaxed tracking-wider text-red-400/80">
                  FOR RESEARCH USE ONLY — NOT FOR HUMAN CONSUMPTION
                </p>
                <div className="mb-5 flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.2em] text-[#A3A3A3]">SUBTOTAL</span>
                  <span data-testid="cart-total" className="font-display text-2xl text-[#F9F6F0]">
                    ${total.toFixed(2)}
                  </span>
                </div>
                <button
                  data-testid="cart-checkout-btn"
                  onClick={goCheckout}
                  className="group flex w-full items-center justify-center gap-3 bg-[#00ff72] px-6 py-4 font-mono text-xs tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0]"
                >
                  PROCEED TO CHECKOUT
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
